import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppSelector } from "../../../store";
import { storedLocale } from "../../Authentication/util";
import { useLazyAboEnrichment } from "./useLazyAboEnrichment";
import { useLazyProfileImages } from "./useLazyProfileImages";
import { useLazyWhatsAppEnrichment } from "./useLazyWhatsAppEnrichment";
import { getAffAboId, isChinaAff, normalizeEnrichedData } from "../common/ActionReportListView/utils/actionReportUtils";
import type { ActionReportRow, FieldConfig } from "../common/ActionReportListView/types";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { CUSTOMER_BUSINESS_NATURES } from "../../../common/enums/businessNatures";

export interface UseActionReportListProps {
  rows: ActionReportRow[];
  reportId: string;
  period: string;
  fieldConfig: FieldConfig[];
  batchSize?: number;
  skipEnrichment?: boolean;
  enrichWhatsApp?: boolean;
}

export interface UseActionReportListReturn {
  kpisToFetch: string[];
  onRowsRendered: (params: { startIndex: number; stopIndex: number }) => void;
}

export const useActionReportList = ({
  rows,
  reportId,
  period,
  fieldConfig,
  batchSize,
  skipEnrichment = false,
  enrichWhatsApp = false,
}: UseActionReportListProps) => {
  const { user } = useAppSelector((state) => state.boot);

  let kpisToFetch = fieldConfig.map((field) => field.id);

  // Rename personalPVRequirement to ppvARRequirement before sending to enrich API
  kpisToFetch = kpisToFetch.map((kpi) => (kpi === KPI_ID.PERSONAL_PV_REQ ? KPI_ID.PERSONAL_PV_REQ_AR : kpi));

  // Set of aff-abos that are customers (business nature 3/4/17). The API uses this list to
  // source their personalPV/annualPPV columns from customer orders instead of los-lite.
  const customerAffAbos = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((row) => {
      const affAboId = getAffAboId(row);
      if (affAboId && CUSTOMER_BUSINESS_NATURES.has(Number(row.businessNature))) {
        set.add(affAboId);
      }
    });
    return set;
  }, [rows]);

  // Initialize the lazy ABO enrichment hook
  const { fetchAboData } = useLazyAboEnrichment({
    reportId,
    period,
    kpis: kpisToFetch,
    locale: storedLocale(),
    isoCountryCode: user.isoCountryCode || "US",
    batchSize,
    skipEnrichment,
    transformEnrichedData: normalizeEnrichedData,
    customerAffAbos,
  });

  // Initialize the lazy profile images hook
  const { fetchImages } = useLazyProfileImages();

  // Initialize the lazy WhatsApp number enrichment hook (independent of skipEnrichment)
  const { fetchWhatsAppNumbers } = useLazyWhatsAppEnrichment({
    period,
    locale: storedLocale(),
    isoCountryCode: user.isoCountryCode || "US",
  });

  /**
   * Callback for virtualized list to trigger data fetching when rows become visible
   */
  const onRowsRendered = useCallback(
    ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
      if (rows.length === 0) return;

      const visibleRows = rows.slice(startIndex, stopIndex + 1);

      if (!skipEnrichment) {
        const visibleAffAboIds = visibleRows
          .filter((row) => !isChinaAff(row.aff))
          .map((row) => getAffAboId(row))
          .filter((id): id is string => id !== null);

        const allAffAboIds = rows
          .filter((row) => !isChinaAff(row.aff))
          .map((row) => getAffAboId(row))
          .filter((id): id is string => id !== null);

        if (visibleAffAboIds.length > 0) {
          fetchAboData(visibleAffAboIds, allAffAboIds);
        }
      }

      // Fetch profile images for visible rows that have imageRefUrl
      const visibleRowsWithImages = visibleRows.filter((row) => !isChinaAff(row.aff) && row.imageRefUrl);
      if (visibleRowsWithImages.length > 0) {
        fetchImages(visibleRowsWithImages);
      }

      // Fetch WhatsApp numbers for visible rows, independent of skipEnrichment
      if (enrichWhatsApp) {
        const visibleNonChinaRows = visibleRows.filter((row) => !isChinaAff(row.aff));
        if (visibleNonChinaRows.length > 0) {
          fetchWhatsAppNumbers(visibleNonChinaRows);
        }
      }
    },
    [rows, fetchAboData, fetchImages, skipEnrichment, enrichWhatsApp, fetchWhatsAppNumbers],
  );

  // Track whether the initial fetch has been triggered for the current rows
  const initialFetchDoneRef = useRef<string | null>(null);

  // Trigger an initial fetch for the first batch of rows when data changes (e.g., tab switch).
  // Virtuoso's rangeChanged may not fire if the scroll position hasn't changed,
  // leaving KPIs in a loading state until the user scrolls.
  useEffect(() => {
    if (rows.length === 0) return;

    // Create a stable key from the first few row IDs to detect when rows actually change
    const rowsKey = rows
      .slice(0, 5)
      .map((row) => getAffAboId(row))
      .join(",");

    if (initialFetchDoneRef.current === rowsKey) return;
    initialFetchDoneRef.current = rowsKey;

    const initialBatchSize = Math.min(rows.length, 10);
    const initialRows = rows.slice(0, initialBatchSize);

    if (!skipEnrichment) {
      const visibleAffAboIds = initialRows
        .filter((row) => !isChinaAff(row.aff))
        .map((row) => getAffAboId(row))
        .filter((id): id is string => id !== null);

      const allAffAboIds = rows
        .filter((row) => !isChinaAff(row.aff))
        .map((row) => getAffAboId(row))
        .filter((id): id is string => id !== null);

      if (visibleAffAboIds.length > 0) {
        fetchAboData(visibleAffAboIds, allAffAboIds);
      }
    }

    // Fetch profile images for initial rows that have imageRefUrl
    const initialRowsWithImages = initialRows.filter((row) => !isChinaAff(row.aff) && row.imageRefUrl);
    if (initialRowsWithImages.length > 0) {
      fetchImages(initialRowsWithImages);
    }

    // Fetch WhatsApp numbers for initial rows, independent of skipEnrichment
    if (enrichWhatsApp) {
      const initialNonChinaRows = initialRows.filter((row) => !isChinaAff(row.aff));
      if (initialNonChinaRows.length > 0) {
        fetchWhatsAppNumbers(initialNonChinaRows);
      }
    }
  }, [rows, fetchAboData, fetchImages, skipEnrichment, enrichWhatsApp, fetchWhatsAppNumbers]);

  return {
    kpisToFetch,
    onRowsRendered,
  };
};
